import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormSetValue, UseFormWatch, Path } from 'react-hook-form';
import identity from 'lodash/identity';

type SimpleStorage = {
  clear(key: string): void
  store(key: string, value: any): void
  restore<T>(key: string): T | null
}

function createSimpleStorage(): SimpleStorage {
  const storage: Storage = localStorage;

  const clear = (key: string) => storage.removeItem(key);
  const store = (key: string, value: any)=> storage.setItem(key, JSON.stringify(value));

  function restore<T>(key: string): T | null {
    try {
      const value = storage.getItem(key);

      if (!value) {
        return null;
      }

      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  return { clear, store, restore };
}

const simpleStorage = createSimpleStorage();

export function clearKeys(keys: string[]) {
  keys.forEach(simpleStorage.clear);
}

export type Props<T extends FieldValues> = {
  isEnabled?: boolean;
  isDirty: boolean
  isSubmitSuccessful: boolean;
  watch: UseFormWatch<T>
  setValue: UseFormSetValue<T>
  transformInput?: (value: any) => any
  transformOutput?: (value: any) => any
}

export function useFormPersist<T extends FieldValues>(storageKey: string, props: Props<T>) {
  const {
    isEnabled = true,
    isDirty,
    isSubmitSuccessful,
    watch,
    setValue,
    transformInput = identity,
    transformOutput = identity,
  } = props;
  const isRestored = useRef(false);
  const formValues = watch();

  useEffect(() => {
    if (!isEnabled || isRestored.current) {
      return;
    }

    const restoredValues: any = simpleStorage.restore(storageKey);

    if (!restoredValues) {
      return;
    }

    const values = transformOutput(restoredValues);

    Object.keys(values).forEach((key) => {
      let value= values[key];

      setValue(key as Path<T>, value);
    });

    isRestored.current = true;
  }, [isEnabled, storageKey, setValue, transformOutput]);

  useEffect(() => {
    if (!isEnabled || isSubmitSuccessful || !isDirty) {
      return;
    }

    const valuesToStore  = transformInput(formValues);

    simpleStorage.store(storageKey, valuesToStore);
  }, [isEnabled, isSubmitSuccessful, isDirty, storageKey, formValues, transformInput]);
}
